import type { IAPI, IAPIMessage } from '../../apiResponse';
import { createApiResponseSchema } from '../../apiResponse';
import { getClientConfig } from '../configuration/client-instance';
import { clearProviderHeadersCache } from '../requestBuilder/resolve-provider-headers';
import { APIResponseError } from './api-response-error';
import { CloudflareError } from './cloudflare-error';
import { detectCloudflareReason } from './detect-cloudflare-reason';
import { isHtmlResponse } from './is-html-response';
import type { IMakeRawRequestParams } from './make-raw-request';
import { makeRawRequest } from './make-raw-request';
import { makeRawRequestWithRetries } from './make-raw-request-with-retries';
import { UnauthorizedError } from './unauthorized-error';

function extractRfc9457Detail(data: unknown): string | undefined {
  if (typeof data === 'object' && data !== null && 'detail' in data) {
    const { detail } = data as { detail: unknown };
    if (typeof detail === 'string') {
      return detail;
    }
  }
  return undefined;
}

function parseErrorResponse(responseData: unknown): {
  errorMessages?: IAPIMessage[];
  errorData: unknown;
  errorDetail?: string;
} {
  try {
    const errorAPISchema = createApiResponseSchema();
    const errorResult = errorAPISchema.safeParse(responseData);
    if (errorResult.success) {
      const apiResponse = errorResult.data as IAPI;
      return { errorMessages: apiResponse.messages, errorData: apiResponse.data };
    }
  } catch {}
  return { errorData: responseData, errorDetail: extractRfc9457Detail(responseData) };
}

export async function makeCheckedRequest(params: IMakeRawRequestParams): Promise<Response> {
  let response = await makeRawRequestWithRetries(params);

  if (response.status === 401) {
    clearProviderHeadersCache();
    const config = getClientConfig();
    const freshHeaders = await config.onUnauthorized?.();
    if (freshHeaders) {
      const merged = {
        ...params.options,
        headers: { ...params.options?.headers, ...freshHeaders },
      };
      response = await makeRawRequest({ ...params, options: merged });
    } else {
      throw new UnauthorizedError();
    }
  }

  if (!response.ok) {
    if (isHtmlResponse(response)) {
      const reason = await detectCloudflareReason(response);
      const error = new CloudflareError({ status: response.status, path: params.path, reason });
      getClientConfig().onError?.(error);
      throw error;
    }

    let errorMessages: IAPIMessage[] | undefined;
    let errorData: unknown;
    let errorDetail: string | undefined;
    try {
      const json: unknown = await response.clone().json();
      ({ errorMessages, errorData, errorDetail } = parseErrorResponse(json));
    } catch {}

    const fallback = `API request failed with status ${response.status}`;
    const error = new APIResponseError({
      message: errorDetail ?? fallback,
      status: response.status,
      statusText: response.statusText,
      messages: errorMessages,
      data: errorData,
      path: params.path,
    });
    getClientConfig().onError?.(error);
    throw error;
  }

  return response;
}
